import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import useObservable from 'react-use/lib/useObservable';
import { useHandTracking } from './useHandTracking';
import { useWindowResizeControl } from './useWindowResizeControl';
import { getThemeColors } from './themeColors';
import { isMuted, primeAudio, toggleMuted } from './sound';
import { GraphRunnerLeaderboard } from './GraphRunnerLeaderboard';
import { useGraphRunnerLeaderboard } from './useGraphRunnerLeaderboard';
import { PlayModeButtons } from './PlayModeButtons';
import {
  RELATION_PATCH_CYPHER_TAGLINE,
  RELATION_PATCH_GAME_OVER_TITLE,
  RELATION_PATCH_GAME_TITLE,
  RELATION_PATCH_HUD_LABEL,
  RELATION_PATCH_STARTING,
  RELATION_PATCH_UNRANKED_RUN_MESSAGE,
} from './gameCopy';
import type {
  ControlMode,
  GameState,
  GraphRunnerP5Instance,
  HandTrackingStatus,
  SubmitScoreResult,
} from './types';
import styles from './GraphRunnerGame.module.css';

function handStatusMessage(
  status: HandTrackingStatus,
  errorMessage: string | null,
): string | null {
  switch (status) {
    case 'requesting-camera':
      return 'Requesting camera access…';
    case 'loading-model':
      return 'Loading hand-tracking model…';
    case 'no-hand':
      return 'Raise one hand to the camera — palm facing the screen works best.';
    case 'tracking':
      return 'Hand tracked — move it up and down to steer.';
    case 'denied':
    case 'unsupported':
    case 'error':
      return errorMessage ?? 'Hand control is unavailable.';
    default:
      return null;
  }
}

interface ModeSelectorProps {
  handleModeSelect: (mode: ControlMode) => Promise<void>;
  engineReady: boolean;
  starting: boolean;
  isUnrankedRun: boolean;
}

function ModeSelector({
  handleModeSelect,
  engineReady,
  starting,
  isUnrankedRun,
}: ModeSelectorProps): JSX.Element {
  return (
    <>
      <PlayModeButtons
        onModeSelect={handleModeSelect}
        disabled={!engineReady || starting}
      />
      {starting && (
        <Text variant="body-small" className={styles.overlayHint}>
          {RELATION_PATCH_STARTING}
        </Text>
      )}
      {isUnrankedRun && (
        <Text variant="body-small" className={styles.overlayHint}>
          {RELATION_PATCH_UNRANKED_RUN_MESSAGE}
        </Text>
      )}
    </>
  );
}

export function GraphRunnerGame() {
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const gameApiRef = useRef<GraphRunnerP5Instance | null>(null);
  const themeRef = useRef(getThemeColors());

  const [controlMode, setControlMode] = useState<ControlMode | null>(null);
  const [gameState, setGameState] = useState<GameState>('idle');
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [muted, setMuted] = useState(isMuted);
  const [engineReady, setEngineReady] = useState(false);
  const [lastSubmitResult, setLastSubmitResult] =
    useState<SubmitScoreResult | null>(null);
  const [starting, setStarting] = useState(false);
  const [isUnrankedRun, setIsUnrankedRun] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<{
    userEntityRef: string;
    displayName: string;
  } | null>(null);
  const submittedScoreRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startingRef = useRef(false);

  const handMode = controlMode === 'hand';
  const windowMode = controlMode === 'window';
  const isPlaying = gameState === 'playing';

  const {
    status: handStatus,
    videoRef,
    handYRef,
    errorMessage: handErrorMessage,
  } = useHandTracking(handMode && isPlaying);

  const {
    status: windowStatus,
    normalizedSteerPercent,
    steerYRef,
  } = useWindowResizeControl(windowMode && isPlaying);

  const handStatusRef = useRef(handStatus);
  const windowStatusRef = useRef(windowStatus);
  const controlModeRef = useRef<ControlMode | null>(null);

  useEffect(() => {
    handStatusRef.current = handStatus;
  }, [handStatus]);

  useEffect(() => {
    windowStatusRef.current = windowStatus;
  }, [windowStatus]);

  useEffect(() => {
    controlModeRef.current = controlMode;
  }, [controlMode]);

  const {
    entries: leaderboardEntries,
    canRemoveScores,
    loading: leaderboardLoading,
    error: leaderboardError,
    startSession,
    submitScore,
    deleteScore,
  } = useGraphRunnerLeaderboard();

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      themeRef.current = getThemeColors();
    });
    return () => cancelAnimationFrame(raf);
  }, [themeId]);

  const getSteerTarget = useCallback((): number | null => {
    if (controlModeRef.current === 'hand') {
      if (handStatusRef.current !== 'tracking') {
        return null;
      }
      return handYRef.current;
    }

    if (windowStatusRef.current !== 'active') {
      return null;
    }
    return steerYRef.current;
  }, [handYRef, steerYRef]);

  const getSteerTargetRef = useRef(getSteerTarget);
  getSteerTargetRef.current = getSteerTarget;

  useEffect(() => {
    let disposed = false;
    let instance: GraphRunnerP5Instance | null = null;

    (async () => {
      const [{ default: P5 }, { createGraphRunnerSketch }] = await Promise.all([
        import('p5'),
        import('./sketch'),
      ]);

      if (disposed || !hostRef.current) return;

      const sketch = createGraphRunnerSketch({
        getSize: () => ({
          width: hostRef.current?.clientWidth ?? 320,
          height: hostRef.current?.clientHeight ?? 180,
        }),
        getTheme: () => themeRef.current,
        getHandTarget: () => getSteerTargetRef.current(),
        callbacks: {
          onScoreChange: setScore,
          onLivesChange: setLives,
          onStateChange: setGameState,
        },
      });

      instance = new P5(
        sketch,
        hostRef.current,
      ) as unknown as GraphRunnerP5Instance;
      gameApiRef.current = instance;
      setEngineReady(true);
    })();

    return () => {
      disposed = true;
      instance?.remove();
      gameApiRef.current = null;
    };
  }, []);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      gameApiRef.current?.resizeCanvas(el.clientWidth, el.clientHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleModeSelect = useCallback(
    async (mode: ControlMode) => {
      if (!engineReady || startingRef.current) {
        return;
      }

      startingRef.current = true;
      setStarting(true);
      setIsUnrankedRun(false);

      try {
        const session = await startSession();
        sessionIdRef.current = session.sessionId;
      } catch {
        sessionIdRef.current = null;
        setIsUnrankedRun(true);
      } finally {
        startingRef.current = false;
        setStarting(false);
      }

      controlModeRef.current = mode;
      setControlMode(mode);
      submittedScoreRef.current = null;
      setLastSubmitResult(null);
      primeAudio();
      gameApiRef.current?.startGame();
      rootRef.current?.focus();
    },
    [engineReady, startSession],
  );

  const handleToggleMute = useCallback(() => {
    setMuted(toggleMuted());
  }, []);

  useEffect(() => {
    if (gameState === 'playing') {
      submittedScoreRef.current = null;
      setLastSubmitResult(null);
    }
  }, [gameState]);

  useEffect(() => {
    const sessionId = sessionIdRef.current;
    if (
      gameState !== 'gameover' ||
      score <= 0 ||
      sessionId === null ||
      submittedScoreRef.current !== null
    ) {
      return;
    }

    submittedScoreRef.current = score;
    void submitScore(sessionId, score).then(result => {
      if (result) {
        setLastSubmitResult(result);
      }
    });
  }, [gameState, score, submitScore]);

  const handleRemoveScore = useCallback(
    (userEntityRef: string, displayName: string) => {
      setPendingRemoval({ userEntityRef, displayName });
    },
    [],
  );

  const handleConfirmRemoval = useCallback(() => {
    if (!pendingRemoval) {
      return;
    }
    const { userEntityRef } = pendingRemoval;
    setPendingRemoval(null);
    void deleteScore(userEntityRef);
  }, [deleteScore, pendingRemoval]);

  const inGameStatusMessage =
    isPlaying && handMode
      ? handStatusMessage(handStatus, handErrorMessage)
      : null;

  const serverBest =
    leaderboardEntries.find(entry => entry.isCurrentUser)?.score ?? 0;
  const personalBest = lastSubmitResult?.personalBest ?? serverBest;
  const isNewBest = lastSubmitResult?.improved ?? false;

  return (
    <Box ref={rootRef} tabIndex={-1} className={styles.root}>
      <div ref={hostRef} className={styles.canvasHost} />

      <Flex align="center" justify="between" gap="3" className={styles.hud}>
        <Flex align="baseline" gap="2" className={styles.hudScore}>
          <Text variant="body-small" className={styles.hudLabel}>
            {RELATION_PATCH_HUD_LABEL}
          </Text>
          <Text weight="bold" className={styles.hudValue}>
            {score}
          </Text>
        </Flex>

        <Flex
          align="center"
          gap="2"
          className={styles.livesRow}
          aria-label={`${lives} lives remaining`}
        >
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={
                i < lives ? styles.lifeNodeActive : styles.lifeNodeLost
              }
            />
          ))}
        </Flex>

        <Box className={styles.muteButton}>
          <ButtonIcon
            variant="tertiary"
            size="small"
            aria-label={muted ? 'Unmute sound' : 'Mute sound'}
            onPress={handleToggleMute}
            icon={
              muted ? (
                <VolumeOffIcon fontSize="small" />
              ) : (
                <VolumeUpIcon fontSize="small" />
              )
            }
          />
        </Box>
      </Flex>

      <GraphRunnerLeaderboard
        entries={leaderboardEntries}
        loading={leaderboardLoading}
        error={leaderboardError}
        canRemoveScores={canRemoveScores}
        onRemoveScore={handleRemoveScore}
      />

      {isPlaying &&
        (handMode ? (
          <Box className={styles.cameraPreview} data-status={handStatus}>
            <video
              ref={videoRef}
              className={styles.cameraVideo}
              muted
              playsInline
              autoPlay
            />
            <div className={styles.cameraStatusDot} />
          </Box>
        ) : (
          <Flex
            direction="column"
            gap="1"
            className={styles.viewportPreview}
            data-status={windowStatus}
          >
            <Text variant="body-small" className={styles.viewportPreviewLabel}>
              Steer
            </Text>
            <Text weight="bold" className={styles.viewportPreviewValue}>
              {normalizedSteerPercent !== null
                ? `${normalizedSteerPercent}%`
                : '—'}
            </Text>
            <Text variant="body-small" className={styles.viewportPreviewHint}>
              0 at 80% · 100 at full
            </Text>
            <div className={styles.viewportPreviewStatusDot} />
          </Flex>
        ))}

      {isPlaying && isUnrankedRun && (
        <Text variant="body-small" className={styles.unrankedRunMessage}>
          {RELATION_PATCH_UNRANKED_RUN_MESSAGE}
        </Text>
      )}

      {inGameStatusMessage && (
        <Text variant="body-small" className={styles.inGameStatusMessage}>
          {inGameStatusMessage}
        </Text>
      )}

      {!isPlaying && (
        <Flex
          direction="column"
          align="center"
          gap="3"
          className={styles.overlay}
        >
          {gameState === 'idle' ? (
            <>
              <Text as="h2" weight="bold" className={styles.overlayTitle}>
                {RELATION_PATCH_GAME_TITLE}
              </Text>
              <Text variant="body-medium" className={styles.overlaySubtitle}>
                {RELATION_PATCH_CYPHER_TAGLINE}
              </Text>
              <Text variant="body-medium" className={styles.overlaySubtitle}>
                Fly through broken relationships to patch them back together.
              </Text>
              <ModeSelector
                handleModeSelect={handleModeSelect}
                engineReady={engineReady}
                starting={starting}
                isUnrankedRun={isUnrankedRun}
              />
              {!engineReady && (
                <Text variant="body-small" className={styles.overlayHint}>
                  Loading {RELATION_PATCH_GAME_TITLE}…
                </Text>
              )}
            </>
          ) : (
            <>
              <Text as="h2" weight="bold" className={styles.overlayTitle}>
                {RELATION_PATCH_GAME_OVER_TITLE}
              </Text>
              <Text variant="body-medium" className={styles.overlaySubtitle}>
                You patched {score} relationship{score === 1 ? '' : 's'}.{' '}
                {isNewBest ? 'New best!' : `Best so far: ${personalBest}.`}
              </Text>
              <ModeSelector
                handleModeSelect={handleModeSelect}
                engineReady={engineReady}
                starting={starting}
                isUnrankedRun={isUnrankedRun}
              />
            </>
          )}
        </Flex>
      )}

      <Dialog
        isOpen={pendingRemoval !== null}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setPendingRemoval(null);
          }
        }}
      >
        <DialogHeader>Remove score</DialogHeader>
        <DialogBody>
          <Text>
            Remove {pendingRemoval?.displayName} from the leaderboard?
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" slot="close">
            Cancel
          </Button>
          <Button variant="primary" destructive onPress={handleConfirmRemoval}>
            Remove
          </Button>
        </DialogFooter>
      </Dialog>
    </Box>
  );
}
